import React, { useState } from "react";
import { DownloadVideoModal } from "../../../../components/MenuOptions";
import {
  buildDownloadFileName,
  DownloadType,
  formatFileSize,
  formatName,
  IFile,
  processSingleVideoDownload,
} from "./helpers";
import { useDownloadGate } from "./useDownloadGate";
import { IVideo } from "../hooks/useFetchVideos";
import { getRenamedDownload } from "../../../../utils/downloadHelpers";
import {
  VideoPanel,
  PanelEmpty,
  PanelError,
  PanelSkeleton,
  PanelAction,
  FileRow,
  IconFileTextGlyph,
  IconDownloadGlyph,
} from "./panels";
import type { PanelStatus } from "./panels";

interface Props {
  /** The video's project, so "All" uses the same gate as the header Download. */
  project?: any;
  video: IVideo;
  transcripts: Array<IFile>;
  videoName?: string;
  bucket?: string;
  status: PanelStatus;
  onRetry: () => void;
}

/** Human labels for the four discoverable transcript formats. */
const TYPE_LABEL: Record<string, string> = {
  srt: "SubRip",
  vtt: "WebVTT",
  docx: "Word",
  txt: "Plain text",
};

export function CaptionsList({ video, project, transcripts, videoName, bucket, status, onRetry }: Props) {
  const [downloadSource, setDownloadSource] = useState<string>();
  const [downloadFileName, setDownloadFileName] = useState<string>();
  const [zipping, setZipping] = useState(false);
  const { canDownload } = useDownloadGate(video, project);

  const downloadAll = async () => {
    setZipping(true);
    try {
      const result = await processSingleVideoDownload(
        video,
        DownloadType.TRANSCRIPT,
        `${formatName(video)}-transcripts.zip`,
      );
      if (result.error || !result.source) return;
      setDownloadFileName(undefined);
      setDownloadSource(result.source);
    } finally {
      setZipping(false);
    }
  };

  const handleDownload = async (file: IFile) => {
    const { link, fileName } = await getRenamedDownload({
      bucket,
      key: file.key,
      fileName: buildDownloadFileName(file.key || file.url, videoName || formatName(video), video),
      fallbackUrl: file.url,
    });
    setDownloadFileName(fileName);
    setDownloadSource(link);
  };

  // "SubRip · 4 KB", or just "SubRip" when the probe couldn't read a size
  // (the ranged-GET fallback can't — see helpers `sizeCache`).
  const metaFor = (file: IFile) =>
    [TYPE_LABEL[file.type] || file.type.toUpperCase(), formatFileSize(file.size)]
      .filter(Boolean)
      .join(" · ");

  const body = () => {
    if (status === "loading") return <PanelSkeleton variant="rows" />;
    if (status === "error") {
      return (
        <PanelError
          icon={<IconFileTextGlyph size={24} strokeWidth={1.6} />}
          title="Couldn't load transcripts"
          onRetry={onRetry}
          grow
        />
      );
    }
    if (transcripts.length === 0) {
      return (
        <PanelEmpty
          icon={<IconFileTextGlyph size={24} strokeWidth={1.6} />}
          title="No transcripts"
          body="No caption files were found for this file."
          grow
        />
      );
    }
    return (
      <div className="flex flex-col gap-2.5">
        {transcripts.map((transcript) => (
          <FileRow
            key={transcript.key || transcript.url}
            name={`Transcript .${transcript.type}`}
            meta={metaFor(transcript)}
            onDownload={() => handleDownload(transcript)}
          />
        ))}
      </div>
    );
  };

  return (
    <VideoPanel
      id="panel-transcripts"
      title="Transcripts"
      count={status === "ready" ? transcripts.length : undefined}
      action={
        status === "ready" && transcripts.length > 0 ? (
          <PanelAction
            label={zipping ? "Zipping…" : "All"}
            icon={<IconDownloadGlyph size={13} strokeWidth={2} />}
            onClick={downloadAll}
            disabled={!canDownload || zipping}
            title={canDownload ? "Download all transcripts" : "Not available for download"}
          />
        ) : undefined
      }
      fill
    >
      {body()}
      <DownloadVideoModal
        open={!!downloadSource}
        onClose={() => {
          setDownloadSource(undefined);
          setDownloadFileName(undefined);
        }}
        downloadSource={downloadSource || ""}
        fileName={downloadFileName}
      />
    </VideoPanel>
  );
}
